import React, { useState } from 'react';
import { StackShim as Stack, StackItemShim as StackItem } from '@fluentui/react-migration-v8-v9';
import {
  Button,
  Field,
  FluentProvider,
  Input,
  webLightTheme,
  DrawerHeader,
  DrawerHeaderTitle,
  Drawer,
  DrawerBody,
  makeStyles,
} from '@fluentui/react-components';
import {
  DismissRegular,
  ArrowUndoRegular,
  DocumentSignatureRegular,
  Dismiss24Regular,
} from '@fluentui/react-icons';
import moment from 'moment';
import { useMutation } from '@apollo/client';
import {
  IMilestone,
  MilestoneStatus,
  MilestoneActionType,
  MilestoneRuleType,
  IFinancialStatementProcess,
} from '../Interfaces';
import MilestoneCard from './MilestoneCard';
import TimelineNavItem from './TimelineNav';
import MilestoneContent from './MilestoneContent';
import MilestoneSignoffAction from './MilestoneSignoffAction';
import MilestoneRescindAction from './MilestoneRescindAction';
import { CollapseAllStatus } from '../ProcessDetail';

import {
  COMMENT_MILESTONE_MUTATION,
  GET_ALL_MILESTONE_HISTORY,
} from '../../../../utils/statApi/MilestoneApi';
import MilestoneHistoryList from '../MilestoneHistory';
import LoadingErrorMessage from '../../../common/errorContent/LoadingErrorMessage';

interface IMilestoneListItemProps {
  milestone: IMilestone;
  process: IFinancialStatementProcess;
  openEditPanel: (focusedMilestoneId: number) => void;
  toggleDividendsPanel: () => void;
  allCollapsed: CollapseAllStatus;
  isLast?: boolean;
}

const useListItemStyles = makeStyles({
  timelineWrapper: { marginRight: '30px' },
  milestoneWrapper: { width: '100%' },
});

const MilestoneListItem: React.FunctionComponent<IMilestoneListItemProps> = ({
  milestone,
  process,
  openEditPanel,
  toggleDividendsPanel,
  allCollapsed,
  isLast,
}: IMilestoneListItemProps): JSX.Element => {
  const listItemClasses = useListItemStyles();
  const [cardActionMode, setCardActionMode] = useState(MilestoneActionType.normal);

  const [showComments, setShowComments] = useState<boolean>(false);

  const cardDisabled = milestone.status === MilestoneStatus.notStarted;

  const [
    createMilestoneCommentRequest,
    { loading: createCommentLoading, error: createCommentError },
  ] = useMutation(COMMENT_MILESTONE_MUTATION);
  const [commentText, setCommentText] = React.useState('');

  const getIcon = () => {
    if (cardActionMode !== MilestoneActionType.normal) {
      return DismissRegular;
    }
    if (milestone.status === MilestoneStatus.complete) {
      return ArrowUndoRegular;
    }
    return DocumentSignatureRegular;
  };

  const iconClick = () => {
    if (getIcon() === ArrowUndoRegular) {
      setCardActionMode(MilestoneActionType.rescind);
    } else if (getIcon() === DismissRegular) {
      setCardActionMode(MilestoneActionType.normal);
    } else {
      setCardActionMode(MilestoneActionType.attestation);
    }
  };

  const hasOutstandingMilestoneGates = (): boolean => {
    if (!milestone.milestoneRules || milestone.status === MilestoneStatus.complete) {
      return false;
    }
    const outstandingGates = milestone.milestoneRules.filter((rule) => {
      const isBeforeFilingDueDate =
        moment.utc(new Date()).startOf('day') <= moment(process.filingDueDate);
      if (rule.type === MilestoneRuleType.reasonForViolation && isBeforeFilingDueDate) {
        return false;
      }
      return !rule.isComplete;
    });
    return outstandingGates.length > 0;
  };

  const isCardActionDisabled = (): boolean => {
    return cardDisabled || hasOutstandingMilestoneGates();
  };

  const isComplete = milestone.status === MilestoneStatus.complete;
  const actionText = cardActionMode === MilestoneActionType.normal ? milestone.actionText : '';
  const timelineWrapperClassName = listItemClasses.timelineWrapper;
  const milestoneWrapperClassName = listItemClasses.milestoneWrapper;

  const showProcessExtensionWarning =
    milestone.maxDaysOffset === 0 && process.hasPendingProcessExtensionRequest;

  const onCommentTextChange = (newValue: string) => {
    setCommentText(newValue);
  };
  const onCommentsSaveClick = () => {
    createMilestoneCommentRequest({
      variables: {
        milestoneId: milestone?.id,
        comment: commentText,
      },
      refetchQueries: [GET_ALL_MILESTONE_HISTORY],
    });
    setCommentText('');
  };

  return (
    <>
      <Drawer
        type="overlay"
        modalType="modal"
        size="medium"
        separator
        open={showComments}
        position="end"
        onOpenChange={(event, data) => {
          setShowComments(data.open);
        }}
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close"
                icon={<Dismiss24Regular />}
                onClick={() => {
                  setShowComments(false);
                }}
              />
            }
          >
            <h1>Milestone Information</h1>
          </DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody>
          <FluentProvider theme={webLightTheme}>
            <Stack>
              <h2>{milestone.title}</h2>
              <p>{milestone.description}</p>
              <h3>History</h3>
              <Stack tokens={{ childrenGap: 10 }}>
                <LoadingErrorMessage
                  loading={createCommentLoading}
                  error={createCommentError}
                  actionName="saving the comment to the server"
                />
                <Stack horizontal tokens={{ childrenGap: '10px' }}>
                  <StackItem grow>
                    <Field>
                      <Input
                        value={commentText}
                        onChange={(e, data) => onCommentTextChange(data.value)}
                        placeholder="Add Comment"
                      />
                    </Field>
                  </StackItem>
                  <Button
                    appearance="primary"
                    disabled={commentText.trim().length === 0 || createCommentLoading}
                    onClick={onCommentsSaveClick}
                  >
                    Add Comment
                  </Button>
                </Stack>
              </Stack>
              <MilestoneHistoryList milestone={milestone} />
            </Stack>
          </FluentProvider>
        </DrawerBody>
      </Drawer>
      <div>
        <Stack horizontal>
          <StackItem className={`ms-hiddenMdDown ${timelineWrapperClassName}`} grow={0}>
            <TimelineNavItem
              status={milestone.status}
              isLast={isLast}
              isGoalMilestone={milestone.maxDaysOffset === 0}
            />
          </StackItem>
          <StackItem grow={1} className={milestoneWrapperClassName}>
            <MilestoneCard
              title={milestone.title}
              actionText={actionText}
              commentText={milestone.commentText}
              disabled={isCardActionDisabled()}
              showProcessExtensionWarning={showProcessExtensionWarning}
              status={milestone.status}
              complete={isComplete}
              icon={getIcon()}
              collapseAllStatus={allCollapsed}
              iconClick={iconClick}
              setShowComments={setShowComments}
            >
              {cardActionMode === MilestoneActionType.normal && (
                <MilestoneContent
                  milestone={milestone}
                  openDueDateEditPanel={openEditPanel}
                  toggleDividendsPanel={toggleDividendsPanel}
                  setViewComments={setShowComments}
                  viewComments={showComments}
                />
              )}
              {cardActionMode === MilestoneActionType.attestation && (
                <MilestoneSignoffAction milestone={milestone} submit={setCardActionMode} />
              )}
              {cardActionMode === MilestoneActionType.rescind && (
                <MilestoneRescindAction milestone={milestone} submit={setCardActionMode} />
              )}
            </MilestoneCard>
          </StackItem>
        </Stack>
      </div>
    </>
  );
};

export default MilestoneListItem;
